import React, { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  chatSidebarCollapsedAtom,
  chatSidebarWidthAtom,
} from '../../../../atoms/v3/uiAtoms';
// 不再需要导入 chatAtoms，由 ChatDetail 和 ChatListPage 组件内部处理
import { ChatListPage } from '../../ChatListPage';
import { ChatDetail } from '../../ChatDetail';
import { lastActiveChatViewAtom } from '../../../../atoms/v3/chatAtoms';
import { ChatSidebarResizer } from './ChatSidebarResizer';
import { usePasteUpload } from '../../../../hooks/v3/usePasteUpload';

/**
 * 桌面右侧对话栏组件
 *
 * 实现了一个符合 iOS/iPadOS/macOS 设计规范的右侧对话栏：
 * - 显示对话列表或对话详情
 * - 可以折叠/展开
 * - 对话切换完全在右侧对话栏内部进行，不影响主内容区域
 * - 直接复用现有的 ChatListPage 和 ChatDetail 组件
 */
export const DesktopChatSidebar: React.FC = () => {
  const [chatSidebarCollapsed, setChatSidebarCollapsed] = useAtom(chatSidebarCollapsedAtom);
  const chatSidebarWidth = useAtomValue(chatSidebarWidthAtom);
  const chatView = useAtomValue(lastActiveChatViewAtom);
  const { t } = useTranslation(['common']);

  // 启用全局粘贴监听，桌面端聊天功能需要
  usePasteUpload({ enableGlobalListener: true, onlyInChatPages: true });

  // 不再需要处理路由变化，全局状态已在 index.tsx 中设置
  // 不再需要监听消息，因为我们现在直接使用 atom 状态

  // 切换对话栏折叠状态
  const toggleChatSidebar = () => {
    setChatSidebarCollapsed(!chatSidebarCollapsed);
  };

  // 渲染折叠/展开按钮
  const renderToggleButton = () => (
    <button
      onClick={toggleChatSidebar}
      className={`fixed top-4 w-6 h-6 bg-white dark:bg-ios-dark-gray5 border border-ios-gray5 dark:border-ios-dark-gray4 rounded-l-md flex items-center justify-center text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 transition-colors z-40`}
      style={{
        right: chatSidebarCollapsed ? '0' : `${chatSidebarWidth}rem`
      }}
      aria-label={chatSidebarCollapsed ? t('keymap.expandRightSidebar') : t('keymap.collapseRightSidebar')}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-4 h-4"
      >
        <polyline points={chatSidebarCollapsed ? "15 18 9 12 15 6" : "9 18 15 12 9 6"}></polyline>
      </svg>
    </button>
  );

  // 如果对话栏已折叠，只显示折叠按钮
  if (chatSidebarCollapsed) {
    return renderToggleButton();
  }

  return (
    <>
      {renderToggleButton()}
      <div
        className="fixed top-0 right-0 h-full bg-white dark:bg-ios-dark-gray5 border-l border-ios-gray5 dark:border-ios-dark-gray4 z-10 flex flex-col overflow-hidden"
        style={{
          width: `${chatSidebarWidth}rem`
        }}
      >
        {/* 拖拽调整组件 */}
        <ChatSidebarResizer />

        {chatView === 'list' ? (
          <ChatListPage isEmbedded={true} />
        ) : (
          <ChatDetail isEmbedded={true} />
        )}
      </div>
    </>
  );
};
